/**
 * Spanish.
 *
 * Needs review by a native speaker before shipping; see the note in `ar.ts`.
 * The encryption vocabulary in particular ("número de seguridad", "clave de
 * identidad") is terminology a user has to trust, and a plausible wrong choice
 * is worse than an obvious gap.
 */
import type { MessageKey } from './en.ts';

export const es: Partial<Record<MessageKey, string>> = {
  'app.opening': 'Abriendo la caja fuerte…',
  'app.empty.title': 'Elige una conversación',
  'app.empty.body':
    'Tus mensajes están cifrados de extremo a extremo. El servidor solo pasa sobres cerrados entre vosotros: no puede abrirlos, ni tampoco quien consiga acceder a él.',
  'app.identityAlert.title': 'La clave de identidad ha cambiado',
  'app.identityAlert.body':
    'La clave de identidad de {name} ha cambiado. Puede que haya reinstalado la aplicación, o que alguien esté suplantando su identidad.',
  'app.identityAlert.accept': 'Aceptar la nueva clave',
  'app.identityAlert.dismiss': 'Ignorar',

  'onboarding.invited': '{name} te ha invitado.',
  'onboarding.invited.detail': 'Cuando te registres, tu conversación con esa persona ya estará esperándote.',
  'onboarding.intro':
    'Mensajes cifrados de extremo a extremo. Tus claves se generan aquí, en tu dispositivo, y nunca salen de él.',
  'onboarding.username.label': 'Nombre de usuario',
  'onboarding.username.hint': 'De 3 a 32 caracteres, en un solo sistema de escritura.',
  'onboarding.displayName.label': 'Nombre visible',
  'onboarding.phone.label': 'Número de teléfono — opcional',
  'onboarding.phone.hint':
    'Solo para que te encuentre quien ya tiene tu número. No es el nombre de tu cuenta, no es como inicias sesión, y quitarlo más adelante no se lleva nada más.',
  'onboarding.submit': 'Crear cuenta',
  'onboarding.submit.busy': 'Generando claves…',
  'onboarding.warning':
    'No hay contraseña ni recuperación. Tu identidad es una clave privada guardada en este navegador: si borras los datos del sitio, tendrás que registrarte de nuevo.',
  'onboarding.username.error.too_short': 'Al menos 3 caracteres.',
  'onboarding.username.error.too_long': 'Como máximo 32 caracteres.',
  'onboarding.username.error.bad_start': 'No puede empezar por punto, guion o guion bajo.',
  'onboarding.username.error.disallowed_character': 'Contiene un carácter que no está permitido.',
  'onboarding.username.error.mixed_script':
    'Mezcla dos sistemas de escritura. Elige uno, para que nadie pueda imitar tu nombre.',
  'onboarding.username.error.reserved': 'Ese nombre está reservado.',

  'status.online': 'En línea',
  'status.connecting': 'Conectando…',
  'status.offline': 'Sin conexión',
  'sidebar.invite': 'Invitar a amigos',
  'sidebar.findByPhone': 'Buscar por número de teléfono',
  'sidebar.newChat': 'Nueva conversación',
  'sidebar.search': 'Buscar conversaciones',
  'sidebar.conversations': 'Conversaciones',
  'sidebar.empty': 'Aún no hay conversaciones.',
  'sidebar.noMatches': 'Sin resultados',
  'sidebar.noMessages': 'Aún no hay mensajes',
  'sidebar.group': 'Grupo',
  'sidebar.notifications.on': 'Notificaciones activadas',
  'sidebar.notifications.denied': 'Notificaciones bloqueadas por el navegador',
  'sidebar.notifications.enable': 'Activar notificaciones',

  'thread.back': 'Volver a las conversaciones',
  'thread.typing': '{count, plural, one {{names} está escribiendo…} other {{names} están escribiendo…}}',
  'thread.members': '{count, plural, one {# miembro} other {# miembros}}',
  'thread.verifySafety': 'Verificar el número de seguridad',
  'thread.encrypted':
    'Los mensajes de esta conversación están cifrados de extremo a extremo. Ni siquiera el servidor puede leerlos.',
  'thread.replyingTo': 'Respondiendo a: {preview}',
  'thread.attachment': 'Adjunto',
  'thread.cancelReply': 'Cancelar respuesta',
  'thread.recording': 'Grabando… suelta para enviar',
  'thread.cancelRecording': 'Cancelar grabación',
  'thread.composer': 'Mensaje',
  'thread.send': 'Enviar',
  'thread.voice.unavailable.title':
    'La grabación de voz necesita HTTPS: el navegador bloquea el micrófono en esta dirección',
  'thread.voice.unavailable.label': 'Grabación de voz no disponible sin HTTPS',
  'thread.voice.start': 'Grabar un mensaje de voz',
  'thread.voice.stop': 'Detener y enviar',
  'thread.voice.error.title': 'No se ha podido grabar',

  'bubble.deleted': 'Este mensaje se ha eliminado',
  'bubble.actions': 'Acciones del mensaje',
  'bubble.reply': 'Responder',
  'profile.profiles': 'Perfiles',
  'profile.profiles.explain': 'Cuentas separadas en este dispositivo: trabajo, personal, un negocio. Todas siguen conectadas, así que un mensaje a cualquiera te avisa.',
  'profile.profiles.add': 'Añadir un perfil',
  'profile.profiles.create': 'Crear',
  'profile.profiles.namePlaceholder': 'Trabajo, Personal…',
  'profile.profiles.unnamed': 'Perfil sin nombre',
  'profile.profiles.remove': 'Eliminar perfil',
  'profile.profiles.removeConfirm': '¿Eliminar esta cuenta para siempre?',
  'profile.profiles.warning': 'Un perfil nuevo es una cuenta nueva con su propio nombre de usuario, no otra vista de esta. Tampoco se puede recuperar.',
  'profile.build': 'Compilación {build}',
  'profile.name.edit': 'Cambiar nombre',
  'profile.name.save': 'Guardar',
  'profile.name.cancel': 'Cancelar',
  'profile.name.failed': 'No se pudo cambiar el nombre. Comprueba la conexión e inténtalo de nuevo.',
  'profile.delete': 'Eliminar cuenta',
  'profile.delete.explain': 'Esto elimina tu cuenta del servidor para siempre. Tus claves existen solo aquí, así que no hay vuelta atrás ni nada que recuperar.',
  'profile.delete.others': 'Los mensajes que ya enviaste permanecen en los dispositivos que los recibieron. A nadie se le avisa: lo descubren si te escriben.',
  'profile.delete.confirmPrompt': 'Escribe tu nombre de usuario para confirmar',
  'profile.delete.confirm': 'Eliminar definitivamente',
  'profile.delete.failed': 'No se pudo eliminar la cuenta. Comprueba la conexión e inténtalo de nuevo.',
  'thread.recipientGone': 'Esta persona ya no está en VAV. Tus mensajes no le llegarán.',
  'thread.recipientGone.title': 'Esta cuenta fue eliminada',
  'profile.avatar.change': 'Cambiar foto',
  'profile.avatar.remove': 'Quitar foto',
  'profile.avatar.explain': 'Tu foto está cifrada y solo se envía a las personas con las que hablas. Los desconocidos ven tus iniciales.',
  'avatar.crop.title': 'Ajustar la foto',
  'avatar.crop.hint': 'Arrastra para posicionar, desliza para hacer zoom. Lo que queda dentro del círculo es lo que todos ven.',
  'avatar.crop.zoom': 'Zoom',
  'avatar.crop.loadFailed': 'No se pudo leer esta foto. Puede ser un formato que la app no admite; prueba con otra foto.',
  'call.incoming.audio': 'Llamada de voz entrante',
  'call.incoming.video': 'Videollamada entrante',
  'call.accept': 'Contestar',
  'call.decline': 'Rechazar',
  'call.outgoing': 'Llamando…',
  'call.cancel': 'Cancelar',
  'call.hangup': 'Colgar',
  'call.mute': 'Silenciar',
  'call.unmute': 'Activar sonido',
  'call.cameraOff': 'Apagar cámara',
  'call.cameraOn': 'Encender cámara',
  'call.connecting': 'Conectando…',
  'call.ended.declined': 'Llamada rechazada',
  'call.ended.busy': 'Ocupado',
  'call.ended.hangup': 'Llamada finalizada',
  'call.ended.failed': 'No se pudo iniciar la llamada',
  'call.ended.unanswered': 'Sin respuesta',
  'thread.call.audio': 'Llamada de voz',
  'thread.call.video': 'Videollamada',
  'call.legFailed': 'No se pudo conectar',
  'call.turnMissing': 'El servidor de tránsito (TURN) aún no está configurado; las llamadas entre redes distintas lo necesitan',
  'call.forProfile': 'A tu perfil {name}',
  'call.toSpeaker': 'Altavoz',
  'call.toEarpiece': 'Auricular',
  'onboarding.link.start': 'Ya tengo una cuenta: vincular este dispositivo',
  'onboarding.link.title': 'Vincular este dispositivo',
  'onboarding.link.hint': 'En un dispositivo que ya tenga tu cuenta: Mis datos, Vincular un dispositivo nuevo, y escanea este código o pégalo.',
  'onboarding.link.verifyHint': 'Comprueba que estos dígitos aparecen también en el dispositivo que aprueba.',
  'onboarding.link.copy': 'Copiar código de vinculación',
  'onboarding.link.copied': 'Copiado ✓',
  'onboarding.link.waiting': 'Esperando aprobación…',
  'onboarding.link.failed': 'La vinculación no se completó. Empieza de nuevo en ambos dispositivos.',
  'profile.device.remove': 'Desconectar',
  'profile.logout': 'Cerrar sesión en este dispositivo',
  'profile.logout.explain': 'Tu cuenta permanece en tus otros dispositivos. Todo lo guardado en este dispositivo se borrará de él.',
  'profile.logout.confirm': 'Cerrar sesión',
  'link.approve.stranded': 'Este navegador no tiene tu cuenta, así que no puede aprobar el dispositivo nuevo. Abre el enlace (o pega el código) en la app de VAV en el dispositivo donde está tu cuenta.',
  'link.approve.title': 'Aprobar un dispositivo nuevo',
  'link.approve.account': 'El dispositivo nuevo se unirá a la cuenta {name}.',
  'profile.link.start': 'Vincular un dispositivo nuevo',
  'profile.link.hint': 'Pega el código de vinculación que muestra el dispositivo nuevo.',
  'profile.link.verifyHint': 'Aprueba solo si el dispositivo nuevo muestra los mismos dígitos.',
  'profile.link.approve': 'Aprobar el dispositivo',
  'profile.link.done': 'Dispositivo vinculado',
  'profile.link.failed': 'No se pudo aprobar; puede que el código haya caducado.',
  'thread.date.today': 'Hoy',
  'thread.date.yesterday': 'Ayer',
  'contact.delete': 'Eliminar contacto',
  'contact.delete.explain': 'La conversación y su historial se eliminan solo de este dispositivo. No se le avisa, y su copia sigue siendo suya.',
  'group.leave': 'Salir del grupo',
  'group.leave.explain': 'Los miembros verán que saliste. El grupo y su historial se eliminan de este dispositivo.',
  'contact.title': 'Datos del contacto',
  'contact.verified': 'Verificado',
  'profile.title': 'Mis datos',
  'profile.username': 'Nombre de usuario',
  'profile.thisDevice': 'Este dispositivo',
  'profile.devices': 'Dispositivos',
  'profile.devicesLoading': 'Cargando…',
  'profile.devicesNone': 'No se pudo conectar con el servidor.',
  'profile.deviceCurrent': 'este',
  'profile.install': 'Instalar en este dispositivo',
  'profile.install.action': 'Añadir a la pantalla de inicio',
  'profile.install.already': 'Estás usando la app instalada.',
  'profile.install.ios': 'Abre el menú Compartir y elige «Añadir a pantalla de inicio».',
  'profile.install.unavailable': 'Tu navegador no ofrece instalarla ahora, normalmente porque ya está instalada. Busca VAV en tu lista de apps.',
  'profile.noRecovery': 'Tus claves existen solo en este dispositivo. Borrar los datos del navegador o perder el teléfono pierde la cuenta: no hay recuperación.',
  'bubble.forward': 'Reenviar',
  'bubble.select': 'Seleccionar',
  'bubble.moreReactions': 'Más reacciones',
  'select.cancel': 'Cancelar selección',
  'select.count': '{count, plural, =0 {Seleccionar mensajes} one {# seleccionado} other {# seleccionados}}',
  'forward.title': 'Reenviar a',
  'forward.send': 'Enviar {count, plural, one {# mensaje} other {# mensajes}} a {to, plural, one {# chat} other {# chats}}',
  'thread.voice.blocked.how': 'Abre vav.chat en el navegador Chrome (no en la app instalada), toca el icono a la izquierda de la barra de direcciones → Permisos → Restablecer permisos. Luego pulsa el micrófono otra vez y acepta.',
  'thread.voice.retry': 'Reintentar',
  'thread.voice.blockedByOs': 'Este sitio tiene permiso para el micrófono, así que el bloqueo no está aquí: el navegador no tiene permiso de micrófono del teléfono.',
  'thread.voice.blockedByOs.how': 'Android: Ajustes → Aplicaciones → Chrome (o VAV, si usas la app instalada) → Permisos → Micrófono → Permitir.',
  'thread.voice.sendFailed': 'No se pudo enviar la grabación. Comprueba la conexión e inténtalo de nuevo.',
  'thread.voice.busy': 'El micrófono está permitido pero no se pudo abrir. Probablemente otra app lo esté usando.',
  'thread.voice.busy.how': 'Cierra lo que pueda estar ocupándolo (una llamada, un asistente de voz, una grabadora) e inténtalo de nuevo. Reiniciar el teléfono libera uno atascado.',
  'thread.unreadFrom': '{count, plural, one {# mensaje nuevo} other {# mensajes nuevos}}',
  'thread.voice.blocked': 'El micrófono está bloqueado para este sitio. Un permiso rechazado no se puede volver a pedir: hay que cambiarlo en los ajustes.',
  'thread.voice.noMicrophone': 'No se encontró ningún micrófono en este dispositivo.',
  'push.prompt.title': 'Las notificaciones están desactivadas',
  'push.prompt.body': 'Actívalas y tu teléfono te avisará cuando llegue un mensaje, incluso con la app cerrada.',
  'push.prompt.denied': 'Este sitio tiene bloqueadas las notificaciones. Permítelas en los ajustes del sitio y vuelve.',
  'push.prompt.enable': 'Activar',
  'push.prompt.dismiss': 'Ahora no',
  'quote.you': 'Tú',
  'quote.them': 'Ellos',
  'quote.unavailable': 'Mensaje no disponible',
  'quote.image': 'Foto',
  'quote.video': 'Vídeo',
  'quote.audio': 'Audio',
  'quote.voice': 'Mensaje de voz',
  'schedule.title': 'Enviar más tarde',
  'schedule.explain': 'El mensaje se escribe y se sella ahora, y sale a la hora que elijas. Hasta entonces puedes cancelarlo desde su menú ⋯.',
  'schedule.confirm': 'Programar',
  'bubble.cancelScheduled': 'Cancelar envío programado',
  'bubble.state.scheduled': 'Programado',
  'profile.checkUpdate': 'Buscar actualización',
  'profile.checkUpdate.busy': 'Comprobando…',
  'profile.checkUpdate.current': 'Tienes la última versión.',
  'bubble.edit': 'Editar',
  'bubble.edited': 'editado',
  'thread.editing': 'Editando mensaje',
  'group.edit.title': 'Detalles del grupo',
  'group.edit.name': 'Nombre del grupo',
  'bubble.deleteForMe': 'Eliminar para mí',
  'bubble.save': 'Guardar imagen',
  'bubble.closeImage': 'Cerrar imagen',
  'bubble.deleteForEveryone': 'Eliminar para todos',
  'bubble.decryptFailed': 'No se ha podido descifrar este archivo adjunto.',
  'bubble.decrypting': 'Descifrando…',
  'bubble.state.pending': 'Pendiente',
  'bubble.state.sent': 'Enviado',
  'bubble.state.delivered': 'Entregado',
  'bubble.state.read': 'Leído',
  'bubble.state.failed': 'Fallido',

  'attach.label': 'Adjuntar',
  'attach.camera': 'Cámara',
  'attach.image': 'Foto',
  'attach.video': 'Vídeo',
  'attach.audio': 'Audio',
  'attach.document': 'Documento',
  'attach.error.title': 'No se ha podido adjuntar',

  'media.play': 'Reproducir',
  'media.pause': 'Pausar',
  'media.seek': 'Posición en la grabación',
  'media.decrypting': 'Descifrando…',
  'media.permission.library': 'Sin permiso para acceder a la fototeca',
  'media.permission.camera': 'Sin permiso para acceder a la cámara',
  'media.permission.microphone': 'Sin permiso para acceder al micrófono',

  'invite.title': 'Invitar a amigos',
  'invite.close': 'Cerrar',
  'invite.body':
    'Envía el enlace. Quien lo abra verá que le has invitado, y vuestra conversación estará esperando en cuanto se registre.',
  'invite.preparing': 'Preparando un enlace…',
  'invite.share': 'Compartir',
  'invite.copy': 'Copiar enlace',
  'invite.copied': 'Copiado',
  'invite.copyFailed': 'No se ha podido copiar: selecciona el enlace y cópialo manualmente',
  'invite.caution':
    'El enlace es válido 30 días y se puede reutilizar. Cualquiera que lo tenga puede registrarse y ver que le has invitado: no lo publiques en un sitio público.',
  'invite.message': '{name} te invita a VAV: mensajes cifrados de extremo a extremo.',
  'invite.defaultName': 'Un amigo',

  'newChat.title': 'Nueva conversación',
  'newChat.search': 'Nombre de usuario, nombre completo o número de teléfono',
  'newChat.searchByUsername': 'Buscar por nombre de usuario',
  'newChat.direct': 'Chat',
  'newChat.group': 'Grupo',
  'newChat.groupName': 'Nombre del grupo',
  'newChat.pickHint': 'Elige participantes entre los resultados: tantos como quieras.',
  'newChat.searching': 'Buscando…',
  'newChat.noResults': 'No se han encontrado usuarios con ese nombre.',
  'newChat.createGroup': 'Crear grupo',
  'newChat.openChat': 'Abrir conversación',

  'phone.title': 'Número de teléfono',
  'phone.privacy':
    'Tu número se convierte en un hash en tu dispositivo antes de enviarse. El servidor solo guarda la huella, nunca el número.',
  'phone.mine': 'Mi número, para que puedan encontrarme',
  'phone.save': 'Guardar',
  'phone.saved': 'Guardado',
  'phone.findFriends': 'Buscar amigos',
  'phone.fromContacts': 'Elegir de los contactos',
  'phone.noContactPicker':
    'Este navegador no permite acceder a los contactos. Escribe un número para comprobarlo, o usa Chrome en Android.',
  'phone.checkOne': 'Comprobar un número',
  'phone.search': 'Buscar',
  'phone.noneRegistered': 'Ninguno de esos números está registrado.',
  'phone.invalid': 'Esto no parece un número de teléfono',

  'safety.title': 'Número de seguridad',
  'safety.compare':
    'Compara este número con {name} por otro canal: en persona o por teléfono. Si los números coinciden, no hay nadie en medio.',
  'safety.confirm': 'He verificado este número',
  'safety.changeNote':
    'Si {name} reinstala la aplicación, el número cambiará y recibirás un aviso. Un cambio inesperado conviene comprobarlo antes de seguir enviando.',
  'safety.noSession':
    'Todavía no se han intercambiado claves con {name}. Envía un mensaje y vuelve aquí.',

  'common.cancel': 'Cancelar',
  'push.channel': 'Mensajes',
  'push.newMessage': 'Mensaje nuevo',
  'language.title': 'Idioma',
  'language.note': 'Detectado desde tu navegador. Cámbialo aquí si esa suposición es incorrecta.',
  'language.restart': 'Reinicia la aplicación para terminar de cambiar la dirección del diseño.',
  'app.failed.title': 'No se ha podido iniciar',
  'app.failed.retry': 'Reintentar',
  'phone.invalidHint': 'Prueba con el formato internacional completo, empezando por el código de tu país.',
  'phone.country': 'País',
  'phone.national': 'Tu número, como lo marcarías en tu país',
  'phone.willSave': 'Se guardará como {number}',
  'phone.searchCountry': 'Buscar país',
  'phone.importContacts': 'Buscar en mis contactos',
  'phone.importing': 'Comprobando tus contactos…',
  'phone.importingProgress': 'Comprobando tus contactos… {done} de {total}',
  'phone.importScanning': 'Leyendo tus contactos…',
  'phone.importScanned': '{entries} entradas, {unique, plural, one {# número por comprobar} other {# números por comprobar}}',
  'phone.contactsDenied': 'Se ha denegado el permiso para leer los contactos. Aún puedes comprobar un número a mano.',
  'phone.matched': '{count, plural, =0 {Ninguno de tus contactos está aquí todavía} one {# contacto está aquí} other {# contactos están aquí}}',
  'phone.contactsPrivacy': 'Tus contactos se convierten en hash en este dispositivo. La agenda nunca se sube.',
  'invite.share.subject': 'Únete a mí en VAV',
};
